import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PageContainer from '../layout/PageContainer';
import type { ConsumableLineItem } from '../../models/ConsumableLineItem';
import ConsumableForm from './ConsumableForm';
import { GET_CONSUMABLE_LINE_ITEMS_QUERY } from '../../queries/ConsumableLineItemQueries';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { UPDATE_CONSUMABLE_LINE_ITEMS_MUTATION } from '../../mutations/ConsumableLineItemMutations';

function ConsumableEditForm({
  initialItems,
  onSubmit
}: {
  initialItems: ConsumableLineItem[];
  onSubmit: (consumableLineItems: ConsumableLineItem[]) => Promise<boolean>;
}) {
  const navigate = useNavigate();

  const notifications = useNotifications();

  const handleFormSubmit = useCallback(async (consumableLineItems: ConsumableLineItem[]) => {
    try {
      const success = await onSubmit(
        consumableLineItems
      );

      if (success) {
        notifications.show('Consumables edited successfully.', {
          severity: 'success',
          autoHideDuration: 3000,
        });

        navigate('/dashboard/consumables');
      } else {
        notifications.show(
          'Failed to edit consumables. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    } catch (editError) {
      notifications.show(
        `Failed to edit consumables. Reason: ${(editError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw editError;
    }
  }, [navigate, notifications, onSubmit]);

  return (
    <ConsumableForm
      initialItems={initialItems}
      onSubmit={handleFormSubmit}
    />
  );
}

export default function ConsumableEdit() {
  const [consumableLineItems, setConsumableLineItems] = useState<ConsumableLineItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [getConsumableLineItems] = useLazyQuery(
    GET_CONSUMABLE_LINE_ITEMS_QUERY);

  const [updateConsumableLineItems] = useMutation(
    UPDATE_CONSUMABLE_LINE_ITEMS_MUTATION, {
    refetchQueries: [{ query: GET_CONSUMABLE_LINE_ITEMS_QUERY }],
  });

  const loadData = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const { data, error: getConsumableLineItemsError } = await getConsumableLineItems();

      setIsLoading(false);
      setError(null);

      if (getConsumableLineItemsError) {
        setError(getConsumableLineItemsError);
      }

      if (data?.consumableLineItems) {
        setConsumableLineItems(data.consumableLineItems);
      } else {
        setError({ message: 'No consumables line items found' } as Error);
      }

    } catch (showDataError) {
      setIsLoading(false);
      setError(showDataError as Error);
    }
    setIsLoading(false);
  }, [getConsumableLineItems]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = useCallback(
    async (items: ConsumableLineItem[]) => {
      setIsLoading(true);

      const { data: result } = await updateConsumableLineItems({
        variables: {
          consumableLineItemModels: items.map((item) => ({
            id: item.id,
            description: item.description,
            consumableLineItemUseType: item.consumableLineItemUseType,
            orderQuantity: item.orderQuantity,
            unitQuantity: item.unitQuantity,
            batchQuantity: item.batchQuantity,
            unitCost: item.unitCost,
            shippingCost: item.shippingCost
          })),
        },
      })

      setIsLoading(false);

      return result?.updated === true;
    },
    [updateConsumableLineItems],
  );

  const renderEdit = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return <ConsumableEditForm
      initialItems={consumableLineItems}
      onSubmit={handleSubmit} />;
  }, [isLoading, error, consumableLineItems, handleSubmit]);

  return (
    <PageContainer
      title={'Edit Consumables'}
      breadcrumbs={[
        { title: 'Edit' },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}
